import { ActivatedRoute, Params, Router } from '@angular/router';
import { Company, defaultPageNumber, Folder, Subfolder } from './models/models';
import { BehaviorSubject, catchError, filter, NEVER, Subscription, switchMap, tap } from 'rxjs';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { FolderService } from './services/folder.service';
import { DocumentService } from './services/document.service';
import { MatTooltip } from '@angular/material/tooltip';
import { MatButton } from '@angular/material/button';
import { AsyncPipe } from '@angular/common';
import { FiltersComponent } from './components/filters/filters.component';
import { SkeletonComponent } from './components/skeleton/skeleton.component';
import { FolderComponent } from './components/folder/folder.component';
import { UploadFileComponent } from './components/upload-file/upload-file.component';
import { NewFolderComponent } from './components/new-folder/new-folder.component';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatIcon } from '@angular/material/icon';

interface PageConfig {
  pageNumber: number;
  params: Params | null;
}

@Component({
  selector: 'app-documents',
  templateUrl: './documents.component.html',
  styleUrls: ['./documents.component.scss'],
  imports: [MatTooltip, MatButton, AsyncPipe, FiltersComponent, SkeletonComponent, FolderComponent, MatIcon],
  standalone: true
})
export class DocumentsComponent implements OnInit, OnDestroy {
  currentFolder: Folder | null = null;
  isLoading = true;
  companies: Company[] = [];
  pageConfig$ = new BehaviorSubject<PageConfig>({ pageNumber: defaultPageNumber, params: null });
  readonly defaultPageNumber = defaultPageNumber;

  private subscription = new Subscription();

  constructor(
    private dialog: MatDialog,
    private route: ActivatedRoute,
    private folderService: FolderService,
    private router: Router,
    private documentService: DocumentService,
    private matSnackBar: MatSnackBar
  ) {}

  ngOnInit() {
    this.subscription.add(
      this.route.queryParams
        .pipe(tap(params => this.updateConfigWith({ params, pageNumber: defaultPageNumber })))
        .subscribe()
    );

    this.subscription.add(
      this.pageConfig$
        .pipe(
          switchMap(() => {
            return this.getDocuments();
          })
        )
        .subscribe()
    );

    this.subscription.add(
      this.documentService
        .getCompanies()
        .pipe(tap(customers => (this.companies = customers)))
        .subscribe()
    );
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
  }

  uploadFile() {
    this.dialog
      .open(UploadFileComponent, {
        minWidth: '500px',
        data: { id: this.currentFolder?.folderId || null, companyId: this.currentFolder?.companyId || null }
      })
      .afterClosed()
      .pipe(
        filter(res => !!res),
        switchMap(() => this.getDocuments())
      )
      .subscribe();
  }

  gotToPreviousFolder() {
    this.router.navigate(['.'], {
      queryParams: { folder: this.currentFolder?.parentId },
      relativeTo: this.route,
      queryParamsHandling: 'merge'
    });
  }

  createFolder() {
    this.subscription.add(
      this.dialog
        .open(NewFolderComponent, {
          minWidth: '500px',
          data: {
            folderId: this.currentFolder?.folderId,
            companyId: this.currentFolder?.companyId
          }
        })
        .afterClosed()
        .pipe(
          filter((res: Subfolder) => !!res),
          switchMap(() => this.getDocuments())
        )
        .subscribe()
    );
  }

  onCustomersChange(companyId: number[]) {
    this.router.navigate(['documents'], { queryParams: { companyId }, queryParamsHandling: 'merge' });
  }

  onPageChanged(pageNumber: number) {
    this.updateConfigWith({ pageNumber });
  }

  private getDocuments() {
    this.isLoading = true;
    return this.pageConfig$.pipe(
      switchMap(({ pageNumber, params }) =>
        this.folderService.getFolderFrom(params, pageNumber).pipe(
          tap(folder => {
            this.currentFolder = folder;
            this.isLoading = false;
          }),
          catchError(err => {
            console.error(err);
            this.isLoading = false;
            this.matSnackBar.open('Could not load documents. Please try again later.', 'x');
            return NEVER;
          })
        )
      )
    );
  }

  private updateConfigWith(props: Partial<{ pageNumber: number; params: Params }>) {
    this.pageConfig$.next({ ...this.pageConfig$.value, ...props });
  }
}
